import React, { useState } from "react"
import { Box, Button, Chip, makeStyles, Typography } from "@material-ui/core"
import moment from "moment"
import { ColumnBox, Overdue, PaperBox, RowBox } from "../Boxes"
import {
  useLazyQueryAdminableUser,
  useMutationCreateUserTraining,
  useMutationDeleteUserTraining,
  useQueryUserTrainings,
} from "../../data"
import { toId, useTraining } from "../../utils"
import { TrainingOutlinedSelect } from "../Training"
import { LinearProgress } from "../LinearProgress"
import { FieldSectionHeading } from "../Headings"
import TimeAgo from "../TimeAgo"
import { LoadingSpinner } from "../LoadingSpinner"
import { FormatDateCompact } from "../Format"
import { DeleteConfirmIconButton } from "../Buttons"
import { Icon } from "../Icon"

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: 16,
    lineHeight: "20px",
    fontWeight: "500",
  },
  caption: {
    fontSize: 12,
    lineHeight: "16px",
    color: theme.palette.text.light,
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  progress: {
    width: 100,
  },
  completedChip: {
    fontWeight: "600",
    color: "white",
    backgroundColor: theme.palette.success.main,
    marginRight: theme.spacing(1),
  },
  remove: {
    cursor: "pointer",
    marginLeft: theme.spacing(1),
  },
}))

const PersonTraining = ({ user }) => {
  const classes = useStyles()
  const [trainingCourse, setTrainingCourse] = useState("")
  const { getActiveModuleIndex } = useTraining()
  const {
    data,
    loading: loadingUserTrainings,
    refetch,
  } = useQueryUserTrainings({
    variables: {
      user: toId(user),
    },
  })
  const [loadAdminableUser] = useLazyQueryAdminableUser({ variables: { id: toId(user) } })
  const [createUserTraining, { loading: loadingCreateUserTraining }] = useMutationCreateUserTraining()
  const [deleteUserTraining] = useMutationDeleteUserTraining()

  const handleTrainingChange = (event) => {
    setTrainingCourse(event.target.value)
  }

  const handleAddTraining = async () => {
    await createUserTraining({
      variables: {
        course: trainingCourse,
        user: toId(user),
      },
    })
    refetch()
    loadAdminableUser()
    setTrainingCourse("")
  }

  const handleRemoveTraining = async (item) => {
    await deleteUserTraining({
      variables: {
        id: toId(item),
      },
    })
    refetch()
    loadAdminableUser()
  }

  return (
    <Box mb={5} data-cy="PersonTraining">
      <FieldSectionHeading>Assign training</FieldSectionHeading>
      <RowBox mt={1}>
        <TrainingOutlinedSelect
          value={trainingCourse}
          onChange={handleTrainingChange}
          hasNone={false}
          disabled={loadingCreateUserTraining}
          filterActiveUserTrainings={data?.userTrainings || null}
        />
        <Box ml={1}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddTraining}
            disabled={!trainingCourse || loadingCreateUserTraining}
            data-cy="Button-add-training"
          >
            {!loadingCreateUserTraining && <>Assign</>}
            {loadingCreateUserTraining && <LoadingSpinner size="24px" delay={false} />}
          </Button>
        </Box>
      </RowBox>
      {Boolean(data?.userTrainings.length) && (
        <Box mt={3} data-cy="PersonTraining-list">
          <FieldSectionHeading>Training courses</FieldSectionHeading>
          {[...data.userTrainings]
            .sort((a, b) => (moment(a.createdAt).isAfter(moment(b.createdAt)) ? -1 : 1))
            .map((userTraining, index) => {
              const { id, name, dueAt, status, createdAt } = userTraining
              const completed = Boolean(status?.completedAt)
              const overdue = !completed && dueAt ? moment(dueAt).isBefore(moment()) : false
              const activeModuleIndex = getActiveModuleIndex(userTraining)
              const activeModule = activeModuleIndex > -1 ? status.modules[activeModuleIndex] : null

              return (
                <Box key={id} mt={index > 0 ? 1 : 0}>
                  <PaperBox completed={completed} overdue={overdue}>
                    <RowBox p={2} alignItems="center">
                      {overdue && (
                        <Box mr={1}>
                          <Overdue overdue>
                            <Icon name="warning" />
                          </Overdue>
                        </Box>
                      )}
                      <ColumnBox>
                        <Typography variant="h2" className={classes.title}>
                          {name}
                        </Typography>
                        <Box className={classes.caption}>
                          Assigned <TimeAgo date={createdAt} />
                          {Boolean(dueAt) && (
                            <>
                              ,{" "}
                              <Overdue overdue={overdue} strong>
                                due <FormatDateCompact value={dueAt} />
                              </Overdue>
                            </>
                          )}
                        </Box>
                      </ColumnBox>
                      {!completed && (
                        <RowBox ml="auto" alignContent="center">
                          <Box className={classes.caption} mr={2}>
                            {activeModuleIndex + 1} of {status.modules.length}:{" "}
                            {activeModule ? activeModule.name : "Complete"}
                          </Box>
                          <Box className={classes.progress} mr={2}>
                            <LinearProgress value={status.percentComplete} overdue={overdue} />
                          </Box>
                          <DeleteConfirmIconButton onDelete={() => handleRemoveTraining(userTraining)} />
                        </RowBox>
                      )}
                      {completed && (
                        <RowBox ml="auto">
                          <Box className={classes.caption} pr={1}>
                            <TimeAgo date={status.completedAt} />
                          </Box>
                          <Chip label="Complete" size="small" className={classes.completedChip} />
                        </RowBox>
                      )}
                    </RowBox>
                  </PaperBox>
                </Box>
              )
            })}
        </Box>
      )}
      {!data?.userTrainings && loadingUserTrainings && (
        <Box display="flex" justifyContent="center" mt={3}>
          <LoadingSpinner size={60} />
        </Box>
      )}
    </Box>
  )
}

export { PersonTraining }
